// src/components/LoginForm.js

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Card, Button, Input, Alert } from '../../../shared/components/ui';
import { guestMenuService } from '../../menu/services/guestMenuService';
import authService from '../services/authService';
import Logo from '../../../shared/components/Logo';

const LoginForm = ({ onLoginSuccess }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const data = await guestMenuService.login(email, password);

            // Guardar toda la información del usuario en un solo item de localStorage
            const userData = {
                email: data.user?.email || email,
                preferences: data.preferences || null,
                token: data.token || null
            };
            localStorage.setItem('user_data', JSON.stringify(userData));

            // Actualizar el token en el authService
            if (data.token) {
                authService.setToken(data.token);
            }

            // Llamar callback si existe
            if (onLoginSuccess) {
                onLoginSuccess(data);
            }

            // Redirigir a la página Plan
            navigate('/plan');
        } catch (error) {
            setError(error.message || t('auth.errors.connectionError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo y título */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-2xl">🍽️</span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.login.title')}</h1>
                    <p className="text-gray-600">
                        <Trans
                            i18nKey="auth.login.subtitle"
                            components={{ logo: <Logo inline fontSize={24} /> }}
                        />
                    </p>
                </div>

                {/* Formulario de login */}
                <Card className="shadow-xl" shadow="lg">
                    <Card.Header>
                        <Card.Title className="text-2xl font-bold text-center text-primary-700">
                            {t('auth.login.cardTitle')}
                        </Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('auth.login.emailLabel')}
                                </label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('auth.login.emailPlaceholder')}
                                    required
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('auth.login.passwordLabel')}
                                </label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={t('auth.login.passwordPlaceholder')}
                                    required
                                    className="w-full"
                                />
                            </div>

                            {error && (
                                <Alert variant="error">
                                    <p>{error}</p>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                loading={loading}
                                disabled={loading}
                                className="w-full"
                            >
                                {loading ? t('auth.login.loading') : t('auth.login.button')}
                            </Button>
                        </form>
                    </Card.Content>
                </Card>

                {/* Enlaces adicionales */}
                <div className="text-center mt-6 space-y-4">
                    <p className="text-gray-600">
                        {t('auth.login.noAccount')}{' '}
                        <Link
                            to="/register"
                            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
                        >
                            {t('auth.login.registerLink')}
                        </Link>
                    </p>

                    <Link
                        to="/"
                        className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        {t('auth.login.backHome')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LoginForm;
